import { Box, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  ContractsEmployeesContractVersionFragment,
  ContractsEmployeesEmployeeFragment,
  ContractsEmployeesJobCatalogContextlessPoliciesFragment,
  ContractsEmployeesReferenceContractFragment,
  JobCatalogTreeFragment,
  RoleLevelSalaryDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'

import { CycleInfo } from 'modules/contracts/components/CycleInfo'
import { ContractFlowType } from 'modules/contracts/components/FlowPage'
import FormWrapper from 'modules/contracts/components/FlowPage/FormWrapper'
import { translate } from 'modules/contracts/lib/translate'
import { selectReferenceSalaryRange } from 'modules/job_catalog/lib/salaryUtils'

import RoleLevel from './RoleLevel'
import Salary from './Salary'
import { stepHasErrors } from './validator'

interface Props {
  form: Form
  translations: { [key: string]: string }
  step: number
  type: ContractFlowType
  setIsCurrentStepCompleted: (isCompleted: boolean) => void
  canEditPayrollPolicy?: boolean
  jobCatalogTrees?: JobCatalogTreeFragment[]
  jobCatalogTreeFeatureEnabled?: boolean
  canSeeJobTitle: boolean
  canSeeGrossSalary: boolean
  canSeeCompensations: boolean
  canSeePayrollPolicy: boolean
  contractVersion?:
    | ContractsEmployeesReferenceContractFragment
    | ContractsEmployeesContractVersionFragment
  employee: ContractsEmployeesEmployeeFragment
  legalEntityId: string
  hasPayrollBundle: boolean
  jobCatalogContextlessPolicies: ContractsEmployeesJobCatalogContextlessPoliciesFragment | undefined
}

const RoleLevelSalary = ({
  form,
  translations,
  step,
  type,
  setIsCurrentStepCompleted,
  canEditPayrollPolicy = false,
  jobCatalogTrees,
  jobCatalogTreeFeatureEnabled,
  canSeeJobTitle,
  canSeeGrossSalary,
  canSeeCompensations,
  canSeePayrollPolicy,
  contractVersion,
  employee,
  legalEntityId,
  hasPayrollBundle,
  jobCatalogContextlessPolicies,
}: Props) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const salaryAmount = form.has('salary_amount') ? form.get('salary_amount').value : ''
  const salaryFrequency = form.has('salary_frequency') ? form.get('salary_frequency').value : ''
  const nodeUuid = form.has('job_catalog_tree_node_uuid')
    ? form.get('job_catalog_tree_node_uuid').value
    : undefined

  const { data: roleLevelSalaryData } = useQuery(RoleLevelSalaryDocument, {
    variables: { nodeUuid: nodeUuid || '', employeeId: employee.id, companyId },
    skip: !nodeUuid,
  })

  const salaryRanges = roleLevelSalaryData?.jobCatalog?.nodeAttributesConnection?.nodes
    .filter((node) => node.valueSalaryRange && node.takesPrecedence)
    .map((node) => node.valueSalaryRange)

  const precedenceSalaryRange = selectReferenceSalaryRange(
    salaryRanges?.filter(Boolean) || [],
    employee.defaultLocation?.id ?? undefined,
    employee.legalEntity?.currency || ''
  )
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    setIsCurrentStepCompleted(!stepHasErrors(form, type))
  }, [
    form.get('job_catalog_role_id').value,
    form.get('job_catalog_level_id').value,
    salaryAmount,
    salaryFrequency,
    form.dirtyFieldsKeys.join(),
  ])
  /* eslint-enable react-hooks/exhaustive-deps */

  const isCyclesFeatureEnabled =
    roleLevelSalaryData?.features?.featuresConnection?.nodes[0]?.enabled ?? false
  const cycleInfoData = roleLevelSalaryData?.compensations?.cyclesByEmployeesConnection.nodes[0]

  return (
    <Box gap="s32" paddingBottom="s40">
      <FormWrapper>
        <Text size="400" weight="medium" color="grey1000">
          {translate(translations, 'header')}
        </Text>
      </FormWrapper>
      <Box gap="s24">
        <RoleLevel
          form={form}
          translations={translations}
          type={type}
          jobCatalogTrees={jobCatalogTrees}
          jobCatalogTreeFeatureEnabled={jobCatalogTreeFeatureEnabled}
          canSeeJobTitle={canSeeJobTitle}
          contractVersion={contractVersion}
          employee={employee}
          jobCatalogContextlessPolicies={jobCatalogContextlessPolicies}
        />
        <Salary
          step={step}
          type={type}
          form={form}
          translations={translations}
          contractVersion={contractVersion}
          canEditPayrollPolicy={canEditPayrollPolicy}
          jobCatalogNodeSalary={precedenceSalaryRange}
          jobCatalogTreeFeatureEnabled={jobCatalogTreeFeatureEnabled}
          legalEntityId={legalEntityId}
          legalEntityCountry={employee.legalEntity?.country ?? ''}
          canSeeGrossSalary={canSeeGrossSalary}
          canSeeCompensations={canSeeCompensations}
          canSeePayrollPolicy={canSeePayrollPolicy}
          employee={employee}
          hasPayrollBundle={hasPayrollBundle}
          cycleInfo={
            isCyclesFeatureEnabled && cycleInfoData && cycleInfoData.countryLabel ? (
              <CycleInfo
                countryLabel={cycleInfoData.countryLabel}
                recurrence={cycleInfoData.recurrence}
              />
            ) : null
          }
        />
      </Box>
    </Box>
  )
}

export default observer(RoleLevelSalary)
